import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getClonedPlayerConfigFromClip } from '@goldcast/player-config/src/utils/clipUtils';
import { getAbsolutePlayerTimePure } from '@goldcast/player-config/src/utils/timeUtils';
import { getPlayerConfigElements } from '@goldcast/player-config/src/utils/playerConfigGetters';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import {
  insertElementIntoNewNonOverlappingTrack,
  updatePlayerConfigWithTracks,
  updateClipData,
  createLottieElement
} from '../utils';
import { DEFAULT_LOTTIE_DURATION } from '../constants';
import { AddMenuItem } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelinePlayerActionBar/AddMenu/types';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { saveClipChanges, updateClipWithId, trackClipUpdate } from '@/stores/clip';
import { clipVersionStore } from '@/stores/clipVersionStore';
import { VERSION_EDIT_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import { getSegmentAtCurrentFrame, MIN_SEGMENT_WIDTH } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { zoomLevelStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/zoomLevel';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { brandKit } from '@/stores/brandKit';
import { showSuccessToast, showErrorToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';

export const useMotionGraphics = () => {
  const { clipData, clipId, fps } = useClipsContext();
  const { currentTime } = useRemotionPlayerCurrentTime();
  const { logger } = useAppContext();

  const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
  const compositeSegment = currentSegment && currentSegment.type === SegmentType.COMPOSITE ? currentSegment : null;

  const isDisabled = useMemo(() => {
    return !compositeSegment;
  }, [compositeSegment]);

  const onAdd = useCallback(async () => {
    try {
      const playerConfig = getClonedPlayerConfigFromClip({ ...clipData });
      if (!playerConfig) {
        showErrorToast('Unable to load clip configuration');
        return;
      }

      const currentSec = getAbsolutePlayerTimePure({
        time: currentTime,
        clipData: clipData,
        minSegmentWidth: MIN_SEGMENT_WIDTH,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });
      const startSec = currentSec;
      const durationSec = DEFAULT_LOTTIE_DURATION;

      // Get colors from brand kit with fallbacks
      const colors = brandKit.getSnapshot().colors;
      const defaultColors = ['FF0000', '03FF00', 'FF7400']; // Vibrant fallback colors for motion graphics
      const availableColors = colors || [];

      // Use brand colors if available, otherwise use defaults
      const motionGraphicsColors =
        availableColors.length >= 3
          ? availableColors.slice(0, 3).map(color => color.replace('#', '')) // Remove # prefix for Lottie
          : availableColors.length > 0
            ? [...availableColors.map(color => color.replace('#', '')), ...defaultColors].slice(0, 3)
            : defaultColors;

      const id = uuidv4();
      const element = createLottieElement({
        id,
        fromFrame: Math.ceil(startSec * fps),
        toFrame: Math.ceil((startSec + durationSec) * fps),
        lottie_name: 'crazy_swirl',
        hex_colors: motionGraphicsColors
      });

      const currentTracks = playerConfig?.playerConfig?.tracks ?? [];
      const elementTracks = getPlayerConfigElements(playerConfig);
      const updatedElementTracks = insertElementIntoNewNonOverlappingTrack(elementTracks, element as any);
      const otherTracks = currentTracks.filter(t => t.type !== 'ELEMENTS');
      const updatedPlayerConfig = updatePlayerConfigWithTracks(
        playerConfig as any,
        [...otherTracks, ...updatedElementTracks] as any
      );
      const updatedClip = updateClipData(clipData as any, updatedPlayerConfig as any);
      updateClipWithId(clipId, updatedClip);
      clipVersionStore.emitEdit(clipId, VERSION_EDIT_TYPE.MOTION_GRAPHIC_ADD);
      saveClipChanges({ clipId });
      trackClipUpdate(clipId, 'MOTION_GRAPHICS_ADD');

      showSuccessToast('Motion graphics added successfully');
    } catch (error) {
      logger.error('Failed to add motion graphics:', error);
      showErrorToast('Failed to add motion graphics');
    }
  }, [clipData, clipId, fps, currentTime, logger]);

  const addMotionGraphicsMenuItem: AddMenuItem = useMemo(
    () => ({
      id: 'add-motion-graphics',
      label: 'Add Motion Graphics',
      iconName: 'IconStars',
      onClick: onAdd,
      isDisabled,
      showBetaTag: true
    }),
    [onAdd, isDisabled]
  );

  return { addMotionGraphicsMenuItem };
};
